import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { BrandCore } from './BrandCore';
import { BrandNode } from './brand-nodes';

/**
 * The brand kit as a thing you can turn around in your hands.
 *
 * The orbiting nodes are DOM on a projected sphere rather than WebGL, which is
 * the whole reason they can be used and not merely looked at: the text stays
 * real text at the browser's own hinting, the photographs are `img` tags that
 * load and cache like any other, every node is focusable and clickable, and
 * the colours come from our tokens instead of a second set of shader
 * constants. A canvas would have bought free depth-of-field and charged for
 * all of that.
 *
 * The core at the middle IS a shader — see BrandCore. That one cannot be DOM:
 * an image masked into a circle is a picture of a sphere, and no arrangement
 * of gradients turns into one that rotates.
 *
 * Positions are Fibonacci-spaced so the nodes distribute evenly however many
 * there are — a golden-angle spiral has no seams and no poles to crowd, which
 * an evenly divided lat/long grid does.
 *
 * Transforms are written straight to the elements in a frame loop. Putting the
 * rotation in React state would re-render every node sixty times a second to
 * change two numbers on each.
 */

/** Radians per second at rest. Slow enough to read while it moves. */
const IDLE_SPIN = 0.055;
/** How quickly a flick decays back towards the idle spin. */
const DRAG_DECAY = 0.94;
/** Perspective depth, in sphere radii. Lower is a wider, more distorted lens. */
const PERSPECTIVE = 2.6;
/** Past this much travel, the gesture was a drag and not a click. */
const CLICK_SLOP = 6;

/**
 * The core sits at the middle of the depth range.
 *
 * Nodes are stacked 0–40 by how near the front they are, so putting the core
 * at 20 makes it an occluder rather than a backdrop: the far half of the
 * sphere passes behind it and the near half in front, which is what stops the
 * orbit reading as a flat ring of chips and starts it reading as a ball.
 */
const CORE_Z = 20;

interface Placed {
    /** Unit-sphere position, before rotation. */
    x: number;
    y: number;
    z: number;
}

/** Evenly spaced points on a sphere, by golden angle. */
export function fibonacciSphere(count: number): Placed[] {
    if (count <= 1) return [{ x: 0, y: 0, z: 1 }];
    const golden = Math.PI * (3 - Math.sqrt(5));
    return Array.from({ length: count }, (_, i) => {
        const y = 1 - (i / (count - 1)) * 2;
        const radius = Math.sqrt(Math.max(0, 1 - y * y));
        const theta = golden * i;
        return { x: Math.cos(theta) * radius, y, z: Math.sin(theta) * radius };
    });
}

interface BrandOrbitProps {
    nodes: BrandNode[];
    /** The brand's own name, held still at the centre of what it is made of. */
    centreLabel: string;
    centreImageUrl?: string;
    onSelect: (node: BrandNode) => void;
    /**
     * Open the brand's own name.
     *
     * The centre is not a node — it is what the nodes are about — so without
     * this the one field with no orbiting value is the one field that cannot
     * be reached, which is exactly the field an unnamed brand needs.
     */
    onSelectCentre: () => void;
    /** The field whose panel is open, drawn as held. */
    activeField?: string | null;
    /** The brand's palette, which the core is made of. */
    coreColors?: string[];
    className?: string;
}

export const BrandOrbit: React.FC<BrandOrbitProps> = ({
    nodes, centreLabel, centreImageUrl, onSelect, onSelectCentre, activeField, coreColors, className,
}) => {
    const stageRef = useRef<HTMLDivElement>(null);
    const nodeRefs = useRef<(HTMLButtonElement | null)[]>([]);

    const points = useMemo(() => fibonacciSphere(nodes.length), [nodes.length]);
    /** Read inside the frame loop, which must not close over stale state. */
    const openIdRef = useRef<string | null>(null);

    /** Rotation and its velocity, kept out of state — see the note above. */
    const spin = useRef({ y: 0, x: -0.18, vy: IDLE_SPIN, vx: 0, dragging: false });
    const drag = useRef({ x: 0, y: 0, moved: 0, captured: false });

    /**
     * The node under the pointer, opened to its full text.
     *
     * State rather than a ref, because unlike rotation this one changes what
     * is rendered — and it changes on hover, not sixty times a second.
     *
     * Hovering also stops the sphere. A node that expands while still turning
     * slides out from under the cursor as you start to read it, which closes
     * it again; and the reason to look closely at a node is the reason not to
     * want it moving.
     */
    const [openId, setOpenId] = useState<string | null>(null);
    const frozen = useRef(false);
    frozen.current = openId !== null;
    openIdRef.current = openId;

    useEffect(() => {
        const stage = stageRef.current;
        if (!stage) return undefined;

        /*
         * Someone who has asked for less motion gets a still sphere, not a
         * slower one: the objection is to movement, and this one never stops
         * on its own. It stays fully draggable, which is motion they asked for.
         */
        const still = !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        if (still) spin.current.vy = 0;

        let frame = 0;
        let last = performance.now();

        const draw = (now: number) => {
            const dt = Math.min(0.05, (now - last) / 1000);
            last = now;
            const s = spin.current;

            if (!s.dragging && !frozen.current) {
                s.y += s.vy * dt;
                s.x += s.vx * dt;
                // Back towards the resting spin, so a flick fades rather than
                // either stopping dead or orbiting forever at speed.
                if (!still) s.vy += (IDLE_SPIN - s.vy) * (1 - DRAG_DECAY) * 6;
                s.vx *= DRAG_DECAY;
                // Never past the poles: an over-rotated sphere reads as broken.
                s.x = Math.max(-0.6, Math.min(0.6, s.x));
            }

            const radius = Math.min(stage.clientWidth, stage.clientHeight) * 0.38;
            const cosY = Math.cos(s.y);
            const sinY = Math.sin(s.y);
            const cosX = Math.cos(s.x);
            const sinX = Math.sin(s.x);

            for (let i = 0; i < points.length; i++) {
                const el = nodeRefs.current[i];
                const p = points[i];
                if (!el || !p) continue;

                // Y then X, so dragging up and down tilts the axis you see.
                const x1 = p.x * cosY + p.z * sinY;
                const z1 = -p.x * sinY + p.z * cosY;
                const y2 = p.y * cosX - z1 * sinX;
                const z2 = p.y * sinX + z1 * cosX;

                const depth = PERSPECTIVE / (PERSPECTIVE - z2);
                const front = (z2 + 1) / 2; // 0 at the back, 1 at the front

                el.style.transform =
                    `translate3d(${x1 * radius * depth}px, ${y2 * radius * depth}px, 0) scale(${depth * 0.72})`;
                el.style.opacity = String(0.22 + front * 0.78);
                // An opened node comes to the front of its own sphere, so the
                // text it just revealed is not read through the nodes over it.
                el.style.zIndex = String(openIdRef.current === nodes[i]?.id ? 44 : Math.round(front * 40));
                /*
                 * The far side is scenery, not targets. A node you can barely
                 * see is still a rectangle the pointer can find, and clicking
                 * through the sphere to edit something behind it is worse than
                 * having to turn it round.
                 */
                el.style.pointerEvents = front > 0.45 ? 'auto' : 'none';
            }

            frame = requestAnimationFrame(draw);
        };

        frame = requestAnimationFrame(draw);
        return () => cancelAnimationFrame(frame);
    }, [points, nodes]);

    /*
     * No pointer capture here, deliberately.
     *
     * Capturing on pointerdown made every node unclickable. Once a pointer is
     * captured the browser dispatches the following `click` at the CAPTURE
     * TARGET — this stage — rather than at the button under the cursor, so the
     * nodes' own handlers never ran and the sphere turned beautifully while
     * doing nothing. Capture is taken later, in the move handler, once the
     * gesture has actually travelled far enough to be a drag.
     */
    const onPointerDown = useCallback((e: React.PointerEvent) => {
        spin.current.dragging = true;
        drag.current = { x: e.clientX, y: e.clientY, moved: 0, captured: false };
    }, []);

    const onPointerMove = useCallback((e: React.PointerEvent) => {
        if (!spin.current.dragging) return;
        const dx = e.clientX - drag.current.x;
        const dy = e.clientY - drag.current.y;
        const moved = drag.current.moved + Math.abs(dx) + Math.abs(dy);

        /*
         * Past the slop this is a drag, so take the pointer: it keeps the
         * sphere turning when the cursor leaves the stage mid-flick. Taken
         * here rather than on pointerdown so that a click stays a click — see
         * the note above.
         */
        let captured = drag.current.captured;
        if (!captured && moved > CLICK_SLOP) {
            (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
            captured = true;
        }
        drag.current = { x: e.clientX, y: e.clientY, moved, captured };

        const s = spin.current;
        s.y += dx * 0.006;
        s.x = Math.max(-0.6, Math.min(0.6, s.x - dy * 0.005));
        // Velocity carried from the gesture, so releasing mid-flick coasts on.
        s.vy = dx * 0.35;
        s.vx = -dy * 0.25;
    }, []);

    const endDrag = useCallback((e: React.PointerEvent) => {
        spin.current.dragging = false;
        if (drag.current.captured) {
            (e.currentTarget as Element).releasePointerCapture?.(e.pointerId);
            drag.current.captured = false;
        }
    }, []);

    /** A drag that happens to end on a node is not a click on it. */
    const clickIfNotDragging = useCallback((node: BrandNode) => {
        if (drag.current.moved > CLICK_SLOP) return;
        onSelect(node);
    }, [onSelect]);

    return (
        <div
            ref={stageRef}
            data-testid="brand-orbit"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            className={cn(
                'relative flex cursor-grab touch-none items-center justify-center overflow-hidden select-none',
                'active:cursor-grabbing',
                className,
            )}
        >
            {/*
              * The sphere itself, turning under its own light and made of the
              * vendor's colours. Decorative and pointer-transparent — clicking
              * the brand must reach the button above it, not the canvas behind.
              */}
            <BrandCore
                colors={coreColors}
                className="absolute aspect-square w-[54%] max-w-[460px]"
                style={{ zIndex: CORE_Z }}
            />

            {/* The brand itself, held still while everything it is made of moves. */}
            <button
                type="button"
                data-testid="brand-centre"
                onClick={() => { if (drag.current.moved <= CLICK_SLOP) onSelectCentre(); }}
                className={cn(
                    'absolute z-45 flex flex-col items-center gap-2 rounded-xl px-3 py-2',
                    'transition-colors hover:bg-accent/60 focus-visible:outline-none',
                    activeField === 'brandName' && 'bg-accent/60',
                )}
            >
                {centreImageUrl
                    ? (
                        <img
                            src={centreImageUrl}
                            alt=""
                            className="size-16 rounded-full border bg-background object-contain p-2"
                        />
                    )
                    : <span className="size-2 rounded-full bg-foreground/25" />}
                <span className="max-w-[200px] text-center font-heading text-base font-medium tracking-tight text-foreground">
                    {centreLabel}
                </span>
            </button>

            {nodes.map((node, i) => (
                <button
                    key={node.id}
                    type="button"
                    ref={(el) => { nodeRefs.current[i] = el; }}
                    data-testid={`brand-node-${node.field}`}
                    onClick={() => clickIfNotDragging(node)}
                    /* A swatch and a photograph carry no text to open, so the
                       browser's own tooltip is the only thing that names them. */
                    title={node.kind === 'color' ? node.label : undefined}
                    /*
                     * Opening on hover and on focus, so the full text is
                     * reachable from the keyboard too — the sphere is a list of
                     * buttons to a keyboard, and half a sentence is no more
                     * useful when it is tabbed to than when it is pointed at.
                     * Not opened while dragging: turning the sphere past a node
                     * should not stop it.
                     */
                    onPointerEnter={() => { if (!spin.current.dragging) setOpenId(node.id); }}
                    onPointerLeave={() => setOpenId((id) => (id === node.id ? null : id))}
                    onFocus={() => setOpenId(node.id)}
                    onBlur={() => setOpenId((id) => (id === node.id ? null : id))}
                    className={cn(
                        'absolute origin-center rounded-full border bg-background/90',
                        'transition-[border-color,box-shadow] hover:border-foreground',
                        'focus-visible:border-ring focus-visible:outline-none',
                        // Opened: the whole value, on a settled surface that
                        // reads as lifted off the ones behind it.
                        openId === node.id && node.detail && 'rounded-xl border-foreground bg-background shadow-md',
                        node.empty && 'border-dashed',
                        activeField === node.field && 'border-foreground ring-2 ring-foreground/15',
                        node.kind === 'image' && 'overflow-hidden rounded-xl border-0 p-0',
                        node.kind === 'color' && 'p-0',
                        node.kind !== 'image' && node.kind !== 'color' && 'px-3 py-1.5',
                    )}
                    style={{
                        /*
                         * The typeface, shown in itself. Its name set in the
                         * page font tells the vendor nothing about what was
                         * read off their stylesheets.
                         */
                        fontFamily: node.kind === 'font' && node.value ? `${node.value}, inherit` : undefined,
                        width: node.kind === 'color' ? 44 + node.weight * 20 : undefined,
                        height: node.kind === 'color' ? 44 + node.weight * 20 : undefined,
                        backgroundColor: node.kind === 'color' && !node.empty ? node.value : undefined,
                    }}
                >
                    {node.kind === 'image' && node.value ? (
                        <img src={node.value} alt="" className="size-20 object-cover" draggable={false} />
                    ) : node.kind === 'color' ? (
                        <span className="sr-only">{node.label}</span>
                    ) : (
                        <span
                            className={cn(
                                'block',
                                openId === node.id && node.detail
                                    ? 'max-w-[260px] text-start whitespace-normal'
                                    : 'max-w-[170px] truncate',
                                node.weight > 0.75 ? 'text-sm' : 'text-xs',
                                node.empty ? 'text-muted-foreground' : 'text-foreground',
                            )}
                        >
                            {openId === node.id && node.detail ? node.detail : node.label}
                        </span>
                    )}
                </button>
            ))}
        </div>
    );
};
